import ReactApexChart from "react-apexcharts";

const CHART_COLORS = [
  "#3d8ecc",
  "#2ecc71",
  "#f39c12",
  "#9b59b6",
  "#e74c3c",
  "#1abc9c",
  "#e67e22"
];

function formatAxisValue(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value;
}

const cardStyle = {
  background: "#fff",
  borderRadius: "8px",
  border: "1px solid #e8e8e8",
  padding: "16px 16px 4px",
  marginBottom: "20px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
};

const BarAnalysisChart = ({ title, data, yAxisLabel, variant = "bar" }) => {
  const keys = data?.brands || data?.years || [];
  const noData = !data || !keys.length || !data.data || !data.data.length;

  if (noData) {
    return (
      <div style={cardStyle}>
        <h6 style={{ color: "#3d8ecc", fontWeight: 600, fontSize: "14px", marginBottom: "8px" }}>{title}</h6>
        <p style={{ color: "#aaa", margin: 0 }}>The requested data is not available yet. Please check back later.</p>
      </div>
    );
  }

  const xKey = Object.keys(data.data[0])[0];

  // ─── HEATMAP ─────────────────────────────────────────────────────────────────
  if (variant === "heatmap") {
    const series = keys.map(key => ({
      name: key,
      data: data.data.map(d => ({ x: d[xKey], y: d[key] ?? 0 }))
    }));

    const options = {
      chart: {
        type: "heatmap",
        toolbar: { show: true },
        animations: { enabled: true, speed: 700 },
        fontFamily: "inherit"
      },
      dataLabels: {
        enabled: true,
        formatter: formatAxisValue,
        style: { fontSize: "10px", colors: ["#fff"] }
      },
      colors: ["#3d8ecc"],
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.75,
          radius: 4,
          useFillColorAsStroke: false
        }
      },
      xaxis: {
        labels: { rotate: -30, style: { fontSize: "11px", colors: "#888" } },
        axisBorder: { color: "#e8e8e8" },
        axisTicks: { color: "#e8e8e8" }
      },
      yaxis: { labels: { style: { fontSize: "11px", colors: "#888" } } },
      grid: { borderColor: "#f0f0f0" },
      tooltip: {
        y: { formatter: val => val != null ? val.toLocaleString() : val }
      },
      legend: { show: true, position: "bottom", horizontalAlign: "center", fontSize: "12px" }
    };

    const heatmapHeight = Math.max(200, keys.length * 48 + 80);

    return (
      <div style={cardStyle}>
        <h6 style={{ color: "#3d8ecc", fontWeight: 600, fontSize: "14px", marginBottom: "2px" }}>{title}</h6>
        <p style={{ color: "#999", fontSize: "12px", margin: "0 0 4px" }}>{yAxisLabel} — intensity by brand & month</p>
        <ReactApexChart options={options} series={series} type="heatmap" height={heatmapHeight} />
      </div>
    );
  }

  // ─── COMBO (mixed bar + line for YOY) ─────────────────────────────────────
  if (variant === "combo") {
    const categories = data.data.map(d => d[xKey]);
    // First key = bars (baseline year), remaining = lines (current years)
    const series = keys.map((key, i) => ({
      name: key,
      type: i === 0 ? "bar" : "line",
      data: data.data.map(d => d[key] ?? 0)
    }));

    const options = {
      chart: {
        type: "line",
        toolbar: { show: true },
        animations: { enabled: true, speed: 700 },
        fontFamily: "inherit"
      },
      colors: CHART_COLORS,
      stroke: {
        width: keys.map((_, i) => (i === 0 ? 0 : 3)),
        curve: "smooth"
      },
      plotOptions: {
        bar: {
          columnWidth: "50%",
          borderRadius: 4,
          borderRadiusApplication: "end"
        }
      },
      fill: { opacity: keys.map((_, i) => (i === 0 ? 0.85 : 1)) },
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        labels: { rotate: -30, style: { fontSize: "11px", colors: "#888" } },
        axisBorder: { color: "#e8e8e8" },
        axisTicks: { color: "#e8e8e8" }
      },
      yaxis: {
        title: { text: yAxisLabel, style: { fontSize: "12px", color: "#888", fontWeight: 500 } },
        labels: { formatter: formatAxisValue, style: { colors: "#888" } }
      },
      grid: { borderColor: "#f0f0f0", strokeDashArray: 4 },
      markers: { size: keys.map((_, i) => (i === 0 ? 0 : 4)), hover: { size: 6 } },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: val => val != null ? val.toLocaleString() : val }
      },
      legend: { position: "bottom", horizontalAlign: "center", fontSize: "12px" }
    };

    return (
      <div style={cardStyle}>
        <h6 style={{ color: "#3d8ecc", fontWeight: 600, fontSize: "14px", marginBottom: "2px" }}>{title}</h6>
        <p style={{ color: "#999", fontSize: "12px", margin: "0 0 4px" }}>
          {yAxisLabel} — bars = prior year, line = current year
        </p>
        <ReactApexChart options={options} series={series} type="line" height={340} />
      </div>
    );
  }

  // ─── DEFAULT: grouped bar ──────────────────────────────────────────────────
  const categories = data.data.map(d => d[xKey]);
  const series = keys.map(key => ({
    name: key,
    data: data.data.map(d => d[key] ?? 0)
  }));

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: true },
      animations: { enabled: true, easing: "easeinout", speed: 700 },
      fontFamily: "inherit"
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: keys.length > 3 ? "65%" : "40%",
        borderRadius: 4,
        borderRadiusApplication: "end"
      }
    },
    colors: CHART_COLORS,
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories,
      labels: { rotate: -30, style: { fontSize: "11px", colors: "#888" } },
      axisBorder: { color: "#e8e8e8" },
      axisTicks: { color: "#e8e8e8" }
    },
    yaxis: {
      title: { text: yAxisLabel, style: { fontSize: "12px", color: "#888", fontWeight: 500 } },
      labels: { formatter: formatAxisValue, style: { colors: "#888" } }
    },
    grid: { borderColor: "#f0f0f0", strokeDashArray: 4 },
    fill: { opacity: 1 },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: val => val != null ? val.toLocaleString() : val }
    },
    legend: { position: "top", horizontalAlign: "right", fontSize: "12px" }
  };

  return (
    <div style={cardStyle}>
      <h6 style={{ color: "#3d8ecc", fontWeight: 600, fontSize: "14px", marginBottom: "2px" }}>{title}</h6>
      <p style={{ color: "#999", fontSize: "12px", margin: "0 0 4px" }}>{yAxisLabel} by period</p>
      <ReactApexChart options={options} series={series} type="bar" height={340} />
    </div>
  );
};

export default BarAnalysisChart;
